import React, { useState, useEffect, useRef, useCallback } from 'react'
import ProductCard from '../cards/product-card'
import { ProductGridSkeleton } from '../cards/product-card-skeleton'
import { FilteredProductsResponse } from '@/entities/api/products/product-filters'
import useResponsiveSkeletonCount from '@/hooks/useResponsiveSkeletonCount'
import EmptyProductsState from '../elements/empty-products'
import { useNavigate } from 'react-router-dom'
import CustomersEntity from '@/entities/api/customers/customer'

interface ProductSliderProps {
	filteredProducts?: FilteredProductsResponse
	useExternalPagination?: boolean
	onPageChange?: (page: number, limit: number) => void
	onLimitChange?: (limit: number) => void
	enableInfiniteScroll?: boolean
	onLoadMore?: (nextPage: number) => Promise<void>
	isLoading: boolean
	customerId: string
	customer: CustomersEntity | null
}

const ProductSimpleSlider: React.FC<ProductSliderProps> = ({
	filteredProducts,
	useExternalPagination = false,
	onLimitChange,
	enableInfiniteScroll = false,
	onLoadMore,
	isLoading,
	customerId,
	customer
}) => {
	const [currentPage, setCurrentPage] = useState(1)
	const [productsPerPage, setProductsPerPage] = useState(12)
	const [isLoadingMore, setIsLoadingMore] = useState(false)
	const [hasMorePages, setHasMorePages] = useState(true)
	const [accumulatedProducts, setAccumulatedProducts] = useState<any[]>([])
	const [hasWaitedEnough, setHasWaitedEnough] = useState(false)
	const [isInitialMount, setIsInitialMount] = useState(true)
	const navigate = useNavigate()

	const containerRef = useRef<HTMLDivElement>(null)
	const loadingRef = useRef<HTMLDivElement>(null)
	const isRequestInProgress = useRef(false)
	const waitTimerRef = useRef<NodeJS.Timeout | null>(null)

	const skeletonCount = useResponsiveSkeletonCount({ viewMode: 'grid' })

	useEffect(() => {
		if (isInitialMount) {
			waitTimerRef.current = setTimeout(() => {
				setHasWaitedEnough(true)
			}, 5000)

			return () => {
				if (waitTimerRef.current) {
					clearTimeout(waitTimerRef.current)
				}
			}
		}
	}, [isInitialMount])

	useEffect(() => {
		if (filteredProducts?.products && filteredProducts.products.length > 0) {
			setIsInitialMount(false)
			setHasWaitedEnough(false)
			if (waitTimerRef.current) {
				clearTimeout(waitTimerRef.current)
			}
		}
	}, [filteredProducts?.products])

	useEffect(() => {
		if (!isLoading && !filteredProducts?.products?.length && isInitialMount) {
			waitTimerRef.current = setTimeout(() => {
				setHasWaitedEnough(true)
			}, 5000)
		}

		return () => {
			if (waitTimerRef.current) {
				clearTimeout(waitTimerRef.current)
			}
		}
	}, [isLoading, filteredProducts?.products?.length, isInitialMount])

	const calculateProductsPerPage = () => {
		const width = window.innerWidth

		if (width >= 1280) {
			return 12
		} else if (width >= 1024) {
			return 9
		} else if (width >= 768) {
			return 8
		} else if (width >= 640) {
			return 6
		} else {
			return 4
		}
	}

	const handleLoadMore = useCallback(async () => {
		if (
			!enableInfiniteScroll ||
			!onLoadMore ||
			isRequestInProgress.current ||
			isLoadingMore ||
			!hasMorePages
		) {
			return
		}

		const nextPage = useExternalPagination
			? (filteredProducts?.currentPage || 1) + 1
			: currentPage + 1

		isRequestInProgress.current = true
		setIsLoadingMore(true)

		try {
			await onLoadMore(nextPage)

			if (!useExternalPagination) {
				setCurrentPage(nextPage)
			}
		} catch (error) {
			console.error('Error loading more products:', error)
		} finally {
			setIsLoadingMore(false)
			isRequestInProgress.current = false
		}
	}, [
		enableInfiniteScroll,
		isLoadingMore,
		hasMorePages,
		currentPage,
		filteredProducts?.currentPage,
		useExternalPagination,
	])

	useEffect(() => {
		if (!enableInfiniteScroll || !loadingRef.current) return

		const observer = new IntersectionObserver(
			(entries) => {
				const [entry] = entries
				if (entry.isIntersecting && hasMorePages && !isLoadingMore) {
					handleLoadMore()
				}
			},
			{
				root: null,
				rootMargin: '100px',
				threshold: 0.1,
			}
		)

		const currentLoadingRef = loadingRef.current
		observer.observe(currentLoadingRef)

		return () => {
			if (currentLoadingRef) {
				observer.unobserve(currentLoadingRef)
			}
		}
	}, [enableInfiniteScroll, hasMorePages, isLoadingMore])

	useEffect(() => {
		if (!enableInfiniteScroll || !filteredProducts?.products) return

		const newProducts = filteredProducts.products

		if (filteredProducts.currentPage === 1) {
			setAccumulatedProducts(newProducts)
		} else {
			setAccumulatedProducts((prev) => {
				const existingIds = new Set(
					prev.map((p) => {
						const product = p.product || p
						return product._id || product.id
					})
				)
				const uniqueNewProducts = newProducts.filter((p) => {
					const product = p.product || p
					return !existingIds.has(product._id || product._id)
				})
				return [...prev, ...uniqueNewProducts]
			})
		}
	}, [
		filteredProducts?.products,
		filteredProducts?.currentPage,
		enableInfiniteScroll,
	])

	useEffect(() => {
		if (!enableInfiniteScroll || !filteredProducts) return

		const totalPages = filteredProducts.totalPages || 0
		const currentPageNum = filteredProducts.currentPage || 1

		setHasMorePages(currentPageNum < totalPages)
	}, [filteredProducts, enableInfiniteScroll])

	useEffect(() => {
		if (
			enableInfiniteScroll &&
			filteredProducts?.currentPage === 1 &&
			filteredProducts?.products
		) {
			setAccumulatedProducts(filteredProducts.products)
			setHasMorePages(
				filteredProducts.currentPage < (filteredProducts.totalPages || 1)
			)
			isRequestInProgress.current = false
		}
	}, [
		filteredProducts?.currentPage,
		filteredProducts?.products,
		filteredProducts?.totalPages,
		enableInfiniteScroll,
	])

	useEffect(() => {
		const updateProductsPerPage = () => {
			const newLimit = calculateProductsPerPage()
			setProductsPerPage(newLimit)

			if (useExternalPagination && onLimitChange) {
				onLimitChange(newLimit)
			}
		}

		updateProductsPerPage()

		window.addEventListener('resize', updateProductsPerPage)

		return () => {
			window.removeEventListener('resize', updateProductsPerPage)
		}
	}, [useExternalPagination, onLimitChange])

	useEffect(() => {
		const newLimit = calculateProductsPerPage()
		setProductsPerPage(newLimit)

		if (useExternalPagination && onLimitChange) {
			onLimitChange(newLimit)
		}
	}, [useExternalPagination, onLimitChange])

	const getCurrentProducts = () => {
		if (!filteredProducts?.products) return []

		if (enableInfiniteScroll) {
			return accumulatedProducts.length > 0
				? accumulatedProducts
				: filteredProducts.products
		}

		if (useExternalPagination) {
			return filteredProducts.products
		}

		const startIndex = (currentPage - 1) * productsPerPage
		const endIndex = startIndex + productsPerPage
		return filteredProducts.products.slice(startIndex, endIndex)
	}

	const currentProducts = getCurrentProducts()

	useEffect(() => {
		if (!useExternalPagination) {
			setCurrentPage(1)
		}
	}, [productsPerPage, useExternalPagination])

	const shouldShowLoading = () => {
		if (isLoading) return true
		if (isInitialMount && !hasWaitedEnough && currentProducts.length === 0)
			return true
		return false
	}

	const shouldShowEmpty = () => {
		if (isLoading) return false
		if (isLoadingMore) return false
		if (currentProducts.length > 0) return false
		if (isInitialMount && !hasWaitedEnough) return false
		return true
	}

	const shouldShowProducts = () => {
		return (
			!shouldShowLoading() && !shouldShowEmpty() && currentProducts.length > 0
		)
	}

	return (
		<div className='w-full' ref={containerRef}>
			<div className='relative mb-4'>
				{shouldShowLoading() ? (
					<ProductGridSkeleton
						count={skeletonCount}
						showVariants={true}
						viewMode='grid'
						className='animate-pulse'
					/>
				) : shouldShowEmpty() ? (
					<EmptyProductsState />
				) : shouldShowProducts() ? (
					<div className='grid transition-all duration-300 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-4 gap-2 sm:gap-3 md:gap-4 lg:gap-5'>
						{currentProducts.map((filteredProduct, index) => {
							const product = filteredProduct.product || filteredProduct
							const productId = product._id || product.id || `product-${index}`

							return (
								<ProductCard
									click={() =>
										navigate(`/Producto/${product?.name.replace(/ /g, '-')}/${productId}`)
									}
									key={productId}
									product={product}
									customerId={customerId}
									customer={customer}
								/>
							)
						})}
					</div>
				) : null}
			</div>

			{enableInfiniteScroll && shouldShowProducts() && (
				<div ref={loadingRef} className='w-full py-4'>
					{isLoadingMore && hasMorePages && (
						<div className='flex justify-center items-center'>
							<ProductGridSkeleton
								count={Math.min(skeletonCount, 6)}
								showVariants={true}
								viewMode='grid'
								className='animate-pulse'
							/>
						</div>
					)}
					{!hasMorePages && currentProducts.length > 0 && (
						<div className='text-center py-8'>
							<p className='text-gray-500 text-sm'>
								Has visto todos los productos disponibles
							</p>
						</div>
					)}
				</div>
			)}
		</div>
	)
}

export default ProductSimpleSlider
